
/**
 * Fasting-specific cookie utilities
 */

import { FastingProtocol } from '@/types/fasting';
import { getCookie } from './basic';
import { DEFAULT_COOKIE_OPTIONS, serializeOptions } from './options';

// Cookie names
const COOKIE_PREFIX = 'fast-app-';
const ACTIVE_COOKIE = `${COOKIE_PREFIX}active`;
const PAUSED_COOKIE = `${COOKIE_PREFIX}paused`;
const START_TIME_COOKIE = `${COOKIE_PREFIX}start-time`;
const END_TIME_COOKIE = `${COOKIE_PREFIX}end-time`;
const PAUSED_TIME_COOKIE = `${COOKIE_PREFIX}paused-time`;
const PROTOCOL_COOKIE = `${COOKIE_PREFIX}protocol`;
const PROTOCOL_ID_COOKIE = `${COOKIE_PREFIX}protocol-id`;
const TIME_ELAPSED_COOKIE = `${COOKIE_PREFIX}time-elapsed`;
const WATER_INTAKE_COOKIE = `${COOKIE_PREFIX}water-intake`;
const PAUSE_DURATION_COOKIE = `${COOKIE_PREFIX}pause-duration`;

// Interface for all saved fasting data
export interface SavedFastingData {
  isActive: boolean;
  isPaused: boolean;
  timeElapsed: number;
  selectedProtocol: FastingProtocol;
  waterIntake: number;
  startTime: number | null;
  pausedTime: number | null;
  endTime: number | null;
  totalPauseDuration: number;
}

/**
 * Checks if cookies are allowed and available
 */
const areCookiesAvailable = (): boolean => {
  try {
    // Check if user has given consent (stored in localStorage)
    const hasConsent = localStorage.getItem('cookie-consent');
    
    // If user has explicitly declined, return false
    if (hasConsent === 'false') {
      console.log("[Cookies] User has declined cookie consent");
      return false;
    }
    
    // Test setting and retrieving a cookie
    const testValue = 'test-' + Date.now();
    document.cookie = `${COOKIE_PREFIX}test=${testValue};path=/`;
    const cookieWorks = document.cookie.includes(`${COOKIE_PREFIX}test=${testValue}`);
    
    // Clean up test cookie
    document.cookie = `${COOKIE_PREFIX}test=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    
    console.log("[Cookies] Cookie availability test:", cookieWorks);
    return cookieWorks;
  } catch (error) {
    console.error("[Cookies] Error checking cookie availability:", error);
    return false;
  }
};

/**
 * Saves all fasting app data to cookies
 */
export const saveDataToCookies = (data: SavedFastingData): void => {
  try {
    // First check if cookies are available
    if (!areCookiesAvailable()) {
      console.log("[Cookies] Cookies unavailable - data not saved");
      return;
    }
    
    console.log("[Cookies] Saving fasting data to cookies:", {
      isActive: data.isActive,
      hasStartTime: !!data.startTime,
      timeElapsed: data.timeElapsed,
      protocol: data.selectedProtocol.name
    });
    
    document.cookie = `${ACTIVE_COOKIE}=${data.isActive ? '1' : '0'};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    document.cookie = `${PAUSED_COOKIE}=${data.isPaused ? '1' : '0'};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    document.cookie = `${TIME_ELAPSED_COOKIE}=${data.timeElapsed};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    document.cookie = `${WATER_INTAKE_COOKIE}=${data.waterIntake};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    document.cookie = `${PAUSE_DURATION_COOKIE}=${data.totalPauseDuration};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    
    // Save protocol data
    document.cookie = `${PROTOCOL_ID_COOKIE}=${data.selectedProtocol.id};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    
    try {
      const protocolStr = encodeURIComponent(JSON.stringify(data.selectedProtocol));
      document.cookie = `${PROTOCOL_COOKIE}=${protocolStr};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    } catch (e) {
      console.warn("[Cookies] Error saving protocol to cookie, using ID only", e);
    }
    
    // Save timestamps if available
    if (data.startTime) {
      document.cookie = `${START_TIME_COOKIE}=${data.startTime};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    }
    
    if (data.endTime) {
      document.cookie = `${END_TIME_COOKIE}=${data.endTime};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    }
    
    if (data.pausedTime) {
      document.cookie = `${PAUSED_TIME_COOKIE}=${data.pausedTime};${serializeOptions(DEFAULT_COOKIE_OPTIONS)}`;
    }
    
    // Verify that cookies were actually set
    const verifyCookie = getCookie(ACTIVE_COOKIE);
    if (verifyCookie === null) {
      console.warn("[Cookies] Cookie verification failed - cookies might be blocked");
    } else {
      console.log("[Cookies] Fasting data successfully saved to cookies");
    }
  } catch (error) {
    console.error("[Cookies] Error saving fasting data to cookies:", error);
  }
};

/**
 * Loads all fasting app data from cookies
 */
export const loadDataFromCookies = (protocols: FastingProtocol[]): Partial<SavedFastingData> | null => {
  try {
    // First check if cookies are available
    if (!areCookiesAvailable()) {
      console.log("[Cookies] Cookies unavailable - cannot load data");
      return null;
    }
    
    const isActiveStr = getCookie(ACTIVE_COOKIE);
    const isPausedStr = getCookie(PAUSED_COOKIE);
    const timeElapsedStr = getCookie(TIME_ELAPSED_COOKIE);
    const waterIntakeStr = getCookie(WATER_INTAKE_COOKIE);
    const pauseDurationStr = getCookie(PAUSE_DURATION_COOKIE);
    const protocolIdStr = getCookie(PROTOCOL_ID_COOKIE);
    const protocolStr = getCookie(PROTOCOL_COOKIE);
    const startTimeStr = getCookie(START_TIME_COOKIE);
    const endTimeStr = getCookie(END_TIME_COOKIE);
    const pausedTimeStr = getCookie(PAUSED_TIME_COOKIE);

    // Check if we have any valid cookies at all
    if (!isActiveStr && !startTimeStr && !timeElapsedStr && !protocolIdStr) {
      console.log("[Cookies] No fasting cookies found");
      return null;
    }

    console.log("[Cookies] Found cookies, parsing data...");
    const result: Partial<SavedFastingData> = {};
    
    // Process active state
    if (isActiveStr === '1' || isActiveStr === '0') {
      result.isActive = isActiveStr === '1';
    }
    
    // Process paused state
    if (isPausedStr === '1' || isPausedStr === '0') {
      result.isPaused = isPausedStr === '1';
    }
    
    // Process time elapsed
    if (timeElapsedStr && !isNaN(Number(timeElapsedStr))) {
      result.timeElapsed = Number(timeElapsedStr);
    }
    
    // Process water intake
    if (waterIntakeStr && !isNaN(Number(waterIntakeStr))) {
      result.waterIntake = Number(waterIntakeStr);
    }
    
    // Process pause duration
    if (pauseDurationStr && !isNaN(Number(pauseDurationStr))) {
      result.totalPauseDuration = Number(pauseDurationStr);
    }
    
    // Process protocol - try full protocol object first, then ID
    let protocolFound = false;
    if (protocolStr) {
      try {
        result.selectedProtocol = JSON.parse(decodeURIComponent(protocolStr));
        protocolFound = true;
      } catch (e) {
        console.warn("[Cookies] Error parsing protocol JSON from cookie");
      }
    }
    
    if (!protocolFound && protocolIdStr) {
      const matchingProtocol = protocols.find(p => p.id === protocolIdStr);
      if (matchingProtocol) {
        result.selectedProtocol = matchingProtocol;
        protocolFound = true;
      }
    }
    
    if (!protocolFound && protocols.length > 0) {
      result.selectedProtocol = protocols[0];
    }
    
    // Process timestamps
    if (startTimeStr && !isNaN(Number(startTimeStr))) {
      result.startTime = Number(startTimeStr);
    }
    
    if (endTimeStr && !isNaN(Number(endTimeStr))) {
      result.endTime = Number(endTimeStr);
    }
    
    if (pausedTimeStr && !isNaN(Number(pausedTimeStr))) {
      result.pausedTime = Number(pausedTimeStr);
    }

    console.log("[Cookies] Successfully loaded fasting data from cookies:", {
      isActive: result.isActive,
      hasStartTime: !!result.startTime,
      timeElapsed: result.timeElapsed,
      protocol: result.selectedProtocol?.name
    });
    
    return result;
  } catch (error) {
    console.error("[Cookies] Error loading fasting data from cookies:", error);
    return null;
  }
};

/**
 * Clears all fasting app cookies
 */
export const clearSavedData = (): void => {
  console.log("[Cookies] Clearing all fasting cookies");
  document.cookie = `${ACTIVE_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${PAUSED_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${TIME_ELAPSED_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${WATER_INTAKE_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${PAUSE_DURATION_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${PROTOCOL_ID_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${PROTOCOL_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${START_TIME_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${END_TIME_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${PAUSED_TIME_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};
